import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Language, CandidateStatus, FavoriteCandidate } from '@sourcr/shared';
import { I18N } from '../../lib/i18n';
import { listFavorites, updateFavorite, removeFavorite } from '../../lib/api';
import { GradeChip } from '../../components/ui/grade-chip';
import { CandidateStatusBadge } from '../../components/ui/status-badge';
import { SourceBadge } from '../../components/source-badge';
import { Skeleton } from '../../components/ui/skeleton';

interface FavoritesPageProps {
  lang: Language;
}

const STATUS_CYCLE: CandidateStatus[] = ['pending', 'reviewed', 'contacted', 'declined'];

const S = {
  page: { display: 'flex', flexDirection: 'column' as const, gap: 20, maxWidth: 960 },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  title: { fontSize: 22, fontWeight: 700, color: 'var(--text-primary)', margin: 0 },
  count: { fontSize: 14, color: 'var(--text-muted)' },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: 16,
  },
  card: {
    backgroundColor: 'var(--bg-surface)',
    border: '1px solid var(--border)',
    borderRadius: 12,
    padding: '16px',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 12,
  },
  cardTop: { display: 'flex', gap: 12, alignItems: 'flex-start' },
  avatar: {
    width: 40,
    height: 40,
    borderRadius: '50%',
    backgroundColor: 'var(--accent-subtle)',
    color: 'var(--accent)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
    fontSize: 14,
    flexShrink: 0,
  },
  name: { fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 2 },
  meta: { fontSize: 12, color: 'var(--text-muted)' },
  cardMiddle: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' as const },
  skillChip: {
    padding: '2px 7px',
    backgroundColor: 'var(--bg-overlay)',
    borderRadius: 4,
    fontSize: 11,
    color: 'var(--text-secondary)',
    fontFamily: 'var(--font-mono)',
  },
  divider: { height: 1, backgroundColor: 'var(--border)' },
  cardBottom: { display: 'flex', flexDirection: 'column' as const, gap: 8 },
  statusRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  removeBtn: {
    background: 'none',
    border: 'none',
    color: 'var(--text-muted)',
    cursor: 'pointer',
    fontSize: 13,
    padding: '2px 4px',
    borderRadius: 4,
    lineHeight: 1,
  },
  textarea: {
    width: '100%',
    padding: '8px 10px',
    borderRadius: 8,
    border: '1px solid var(--border)',
    backgroundColor: 'var(--bg-base)',
    color: 'var(--text-primary)',
    fontSize: 12,
    resize: 'none' as const,
    fontFamily: 'var(--font-sans)',
    lineHeight: 1.5,
    outline: 'none',
    boxSizing: 'border-box' as const,
  },
  emptyState: {
    padding: '60px 20px',
    textAlign: 'center' as const,
    color: 'var(--text-muted)',
    fontSize: 14,
  },
  profileLink: {
    fontSize: 12,
    color: 'var(--accent)',
    textDecoration: 'none',
  },
  skeletonCard: {
    backgroundColor: 'var(--bg-surface)',
    border: '1px solid var(--border)',
    borderRadius: 12,
    padding: '16px',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 12,
  },
};

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

export function FavoritesPage({ lang }: FavoritesPageProps) {
  const i = I18N[lang].favorites;
  const navigate = useNavigate();
  const [favs, setFavs] = useState<FavoriteCandidate[]>([]);
  const [loading, setLoading] = useState(true);

  // Debounce notes saves: favId → timeout handle
  const notesTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  useEffect(() => {
    let mounted = true;
    void (async () => {
      try {
        const { favorites: rows } = await listFavorites();
        if (mounted) setFavs(rows);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Cycle status and persist
  async function cycleStatus(fav: FavoriteCandidate) {
    const idx = STATUS_CYCLE.indexOf(fav.status);
    const next = STATUS_CYCLE[(idx + 1) % STATUS_CYCLE.length] ?? 'pending';
    // Optimistic update
    setFavs((fs) => fs.map((f) => (f.id === fav.id ? { ...f, status: next } : f)));
    try {
      await updateFavorite(fav.id, { status: next });
    } catch {
      // Rollback on failure
      setFavs((fs) => fs.map((f) => (f.id === fav.id ? { ...f, status: fav.status } : f)));
    }
  }

  // Debounced notes save (500ms after last keystroke)
  function handleNotesChange(fav: FavoriteCandidate, notes: string) {
    setFavs((fs) => fs.map((f) => (f.id === fav.id ? { ...f, notes } : f)));

    const existing = notesTimers.current.get(fav.id);
    if (existing) clearTimeout(existing);
    const timer = setTimeout(() => {
      void updateFavorite(fav.id, { notes });
      notesTimers.current.delete(fav.id);
    }, 500);
    notesTimers.current.set(fav.id, timer);
  }

  async function handleRemove(id: string) {
    setFavs((fs) => fs.filter((f) => f.id !== id));
    try {
      await removeFavorite(id);
    } catch {
      // If remove fails, refetch to restore
      const { favorites: rows } = await listFavorites();
      setFavs(rows);
    }
  }

  return (
    <div style={S.page}>
      <div style={S.header}>
        <h1 style={S.title}>{i.title}</h1>
        <span style={S.count}>
          {loading ? '…' : `${favs.length} ${i.countLabel}`}
        </span>
      </div>

      {loading ? (
        /* Skeleton loading state */
        <div style={S.grid}>
          {[0, 1, 2, 3].map((n) => (
            <div key={n} style={S.skeletonCard}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <Skeleton width={40} height={40} borderRadius={20} />
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <Skeleton width="70%" height={14} />
                  <Skeleton width="50%" height={12} />
                  <Skeleton width="40%" height={12} />
                </div>
              </div>
              <Skeleton width="100%" height={1} borderRadius={0} />
              <Skeleton width="80%" height={12} />
              <Skeleton width="100%" height={48} borderRadius={8} />
            </div>
          ))}
        </div>
      ) : favs.length === 0 ? (
        <div
          style={{
            padding: '60px 20px',
            textAlign: 'center' as const,
            color: 'var(--text-muted)',
            fontSize: 14,
            display: 'flex',
            flexDirection: 'column' as const,
            alignItems: 'center',
            gap: 12,
          }}
        >
          <span style={{ fontSize: 40 }}>⭐</span>
          <strong style={{ color: 'var(--text-primary)', fontSize: 16 }}>{i.noFavorites}</strong>
          <button
            type="button"
            onClick={() => navigate('/chat')}
            style={{
              marginTop: 4,
              padding: '9px 18px',
              borderRadius: 8,
              border: 'none',
              backgroundColor: 'var(--accent)',
              color: '#fff',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {i.startSearch} →
          </button>
        </div>
      ) : (
        <div style={S.grid}>
          {favs.map((fav) => {
            const c = fav.candidate;
            if (!c) return null;

            return (
              <div key={fav.id} style={S.card}>
                {/* Top: avatar + name + grade + remove */}
                <div style={S.cardTop}>
                  <div style={S.avatar}>{getInitials(c.name)}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={S.name}>{c.name}</div>
                    <div style={S.meta}>{c.title ?? c.location ?? '—'}</div>
                    <a
                      href={c.profileUrl}
                      target="_blank"
                      rel="noreferrer"
                      style={S.profileLink}
                    >
                      {i.viewProfile} ↗
                    </a>
                  </div>
                  <GradeChip score={c.score} size="sm" />
                </div>

                {/* Middle: source + skills */}
                <div style={S.cardMiddle}>
                  <SourceBadge sourceId={c.sourceId} size="sm" />
                  {c.skills.slice(0, 3).map((sk) => (
                    <span key={sk} style={S.skillChip}>{sk}</span>
                  ))}
                </div>

                <div style={S.divider} />

                {/* Bottom: status + notes + remove */}
                <div style={S.cardBottom}>
                  <div style={S.statusRow}>
                    <CandidateStatusBadge
                      status={fav.status}
                      lang={lang}
                      onClick={() => void cycleStatus(fav)}
                    />
                    <button
                      type="button"
                      style={S.removeBtn}
                      onClick={() => void handleRemove(fav.id)}
                      title={i.removeFromFavorites}
                      aria-label={i.removeFromFavorites}
                    >
                      ✕
                    </button>
                  </div>
                  <textarea
                    rows={2}
                    placeholder={i.notesPlaceholder}
                    value={fav.notes ?? ''}
                    onChange={(e) => handleNotesChange(fav, e.target.value)}
                    style={S.textarea}
                    aria-label={`${i.notes} for ${c.name}`}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

    </div>
  );
}
